import { useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PartySocket from "partysocket";
import z from "zod";
import { useOrganization } from "../Auth/UnifiedAuthProvider";

import { Separator } from "@/components/ui/separator";

import { DocumentList } from "./DocumentList";
import { DocumentUploader } from "./DocumentUploader";
import type { KnowledgeDocument } from "./types/Document";
import { KnowledgeDocumentSchema } from "./types/Document";

const DocumentsResponseSchema = z.array(KnowledgeDocumentSchema);

type DocumentsResponse = z.infer<typeof DocumentsResponseSchema>;

export interface DocumentManagerUIProps {
  partySocket: PartySocket;
}

export function DocumentManagerUI({
  partySocket,
}: Readonly<DocumentManagerUIProps>) {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fetchDocuments = async (
    orgId: string,
  ): Promise<KnowledgeDocument[]> => {
    const response = await PartySocket.fetch(
      {
        host: partySocket.host,
        party: "main",
        room: orgId,
        path: "api/knowledge/list",
      },
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return DocumentsResponseSchema.parse(result);
  };

  const deleteDocument = async (key: string) => {
    if (!organization) return;

    const response = await PartySocket.fetch(
      {
        host: partySocket.host,
        party: "main",
        room: organization.id,
        path: "api/knowledge/delete-file",
      },
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, orgId: organization.id }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const parsedResult = z
      .object({ success: z.boolean(), error: z.string().optional() })
      .parse(result);

    if (parsedResult.success) {
      queryClient.invalidateQueries({
        queryKey: ["documents", organization.id],
      });
    } else {
      throw new Error(parsedResult.error ?? "Deletion failed");
    }
  };

  const {
    data: documentsData,
    isLoading,
    isError,
  } = useQuery<DocumentsResponse, Error>({
    queryKey: ["documents", organization?.id],
    queryFn: () => fetchDocuments(organization?.id ?? ""),
    enabled: !!organization?.id,
  });

  const documents = Array.isArray(documentsData) ? documentsData : [];

  const deleteMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["documents", organization?.id],
      });
    },
    onError: (error) => {
      console.error("Error deleting document:", error);
    },
  });

  const handleUpload = async (selectedFiles: File[]) => {
    if (selectedFiles.length === 0 || !organization) return;

    setIsUploading(true);
    try {
      for (const file of selectedFiles) {
        const response = await PartySocket.fetch(
          {
            host: partySocket.host,
            party: "main",
            room: organization.id,
            path: "api/knowledge/upload",
          },
          {
            method: "POST",
            headers: {
              "X-File-Name": encodeURIComponent(file.name),
              "X-Org-Id": encodeURIComponent(organization.id),
            },
            body: file,
          },
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }
      }

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh the documents list
      queryClient.invalidateQueries({
        queryKey: ["documents", organization.id],
      });
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (documentId: string) => {
    if (organization?.id) {
      deleteMutation.mutate(documentId);
    }
  };

  return (
    <div className="flex h-full flex-col space-y-8 p-4">
      <DocumentUploader
        onUpload={handleUpload}
        isUploading={isUploading}
        fileInputRef={fileInputRef}
      />
      <Separator />
      <div className="flex-grow overflow-hidden">
        <DocumentList
          documents={documents}
          isLoading={isLoading}
          isError={isError}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          partySocket={partySocket}
        />
      </div>
    </div>
  );
}
